/**
 * API contracts for the `receiving` module (M3) — zod schemas + inferred types
 * shared by apps/api, apps/web and (via generated types) apps/android.
 *
 * Covers purchase orders, receipt sessions, the §5.7 any-barcode receiving
 * design (GS1 scan resolution, teach-on-first-scan, supplier-SSCC-as-alias,
 * FCL palletize, CO2 cage sessions, the produce fast path) and put-away.
 *
 * OWNERSHIP: the receiving module agent owns this file exclusively.
 * Import from '@warehouse/shared'.
 *
 * CONVENTIONS
 * - Every mutation here is also a CommandBus command type (see
 *   `RECEIVING_COMMANDS`), so the identical payload works from the web app and
 *   from `POST /api/sync/commands` on the offline scanner.
 * - Quantities on the wire are strings (`qtySchema`); `qty`+`unitId` are
 *   converted to the article's base unit ONCE, server-side (ARCHITECTURE §4).
 */
import { z } from 'zod';

import { qtySchema, uuidSchema } from '../schemas/common.js';

// ---------------------------------------------------------------------------
// shared primitives
// ---------------------------------------------------------------------------

/** `YYYY-MM-DD`. Best-before dates are calendar dates, never timestamps. */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

/** A raw scan exactly as the wedge/DataWedge/camera produced it. */
export const scanCodeSchema = z.string().min(1).max(500);

/** Lot numbers come off labels — trim, but never uppercase (they are case-sensitive keys). */
export const lotNoSchema = z.string().trim().min(1).max(64);

const noteSchema = z.string().trim().max(1000);

/**
 * Every command type this module registers with the CommandBus. Web controllers
 * dispatch through the SAME registry the scanner's `/api/sync/commands` uses,
 * so there is exactly one implementation of every write.
 */
export const RECEIVING_COMMANDS = {
  poCreate: 'po.create',
  poUpdate: 'po.update',
  poCancel: 'po.cancel',
  receiptOpen: 'receipt.open',
  receiptLineAdd: 'receipt.lineAdd',
  receiptLineVoid: 'receipt.lineVoid',
  receiptClose: 'receipt.close',
  receiptPalletize: 'receipt.palletize',
  receiptProduceReceive: 'receipt.produceReceive',
  receiptCageScan: 'receipt.cageScan',
  /** Batch form the offline scanner emits: one cage, all its serials. */
  receiptCageSession: 'receipt.cageSession',
  receiptCageClose: 'receipt.cageClose',
  goodsPutaway: 'goods.putaway',
  // Namespaced: master-data owns the plain `barcode.teach` command; this one
  // carries receiving's scan-context variant. RECONCILIATION.md tracks the
  // planned unification of the two teach paths.
  barcodeTeach: 'receiving.barcode.teach',
} as const;
export type ReceivingCommandType = (typeof RECEIVING_COMMANDS)[keyof typeof RECEIVING_COMMANDS];

// ---------------------------------------------------------------------------
// purchase orders
// ---------------------------------------------------------------------------

export const purchaseOrderStatusSchema = z.enum([
  'open',
  'partially_received',
  'received',
  'closed',
  'cancelled',
]);
export type PurchaseOrderStatus = z.infer<typeof purchaseOrderStatusSchema>;

/** Statuses that still accept goods. */
export const PURCHASE_ORDER_RECEIVABLE_STATUSES: readonly PurchaseOrderStatus[] = [
  'open',
  'partially_received',
];

export const purchaseOrderLineDraftSchema = z.object({
  /** Present ⇒ update that line; absent ⇒ append a new one. */
  id: uuidSchema.nullish(),
  articleId: uuidSchema,
  qty: qtySchema,
  /** Omit to order in the article's base unit. */
  unitId: uuidSchema.nullish(),
  sort: z.number().int().min(0).max(9999).optional(),
});
export type PurchaseOrderLineDraft = z.infer<typeof purchaseOrderLineDraftSchema>;

export const purchaseOrderCreateSchema = z.object({
  /** Omit and the server allocates `PO-000123`. BC-synced orders pass their own. */
  poNo: z.string().trim().min(1).max(50).nullish(),
  supplierId: uuidSchema,
  expectedDate: isoDateSchema.nullish(),
  note: noteSchema.nullish(),
  lines: z.array(purchaseOrderLineDraftSchema).min(1).max(500),
});
export type PurchaseOrderCreateInput = z.infer<typeof purchaseOrderCreateSchema>;

export const purchaseOrderUpdateSchema = z.object({
  id: uuidSchema,
  expectedDate: isoDateSchema.nullish(),
  note: noteSchema.nullish(),
  status: purchaseOrderStatusSchema.optional(),
  /** Full replacement of the line set when present; omit to leave lines alone. */
  lines: z.array(purchaseOrderLineDraftSchema).max(500).optional(),
});
export type PurchaseOrderUpdateInput = z.infer<typeof purchaseOrderUpdateSchema>;

export const purchaseOrderCancelSchema = z.object({
  id: uuidSchema,
  reason: noteSchema.nullish(),
});
export type PurchaseOrderCancelInput = z.infer<typeof purchaseOrderCancelSchema>;

export interface PurchaseOrderLineView {
  id: string;
  poId: string;
  articleId: string;
  articleNo: string;
  articleName: string;
  qty: string;
  unitId: string;
  unitCode: string;
  qtyBase: string;
  receivedQtyBase: string;
  /** `qtyBase - receivedQtyBase`, floored at zero — what receiving still expects. */
  openQtyBase: string;
  sort: number;
}

export interface PurchaseOrderListItem {
  id: string;
  poNo: string;
  supplierId: string;
  supplierName: string;
  status: PurchaseOrderStatus;
  expectedDate: string | null;
  note: string | null;
  lineCount: number;
  /** 0-100, rounded — the received progress shown in the list. */
  receivedPct: number;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderDetail extends PurchaseOrderListItem {
  lines: PurchaseOrderLineView[];
  /** Open receipt sessions against this PO, newest first. */
  openReceiptIds: string[];
}

// ---------------------------------------------------------------------------
// receipt sessions
// ---------------------------------------------------------------------------

export const receiptStatusSchema = z.enum(['open', 'closed']);
export type ReceiptStatus = z.infer<typeof receiptStatusSchema>;

/**
 * A receiving session at the dock: against a PO, or **blind** against a
 * supplier. `poId` alone is enough — the supplier is taken from the PO.
 */
export const receiptOpenSchema = z
  .object({
    /**
     * A receipt id the CLIENT minted. Load-bearing for the offline scanner
     * (ARCHITECTURE §6): `ReceiveFlow` opens a session, adds twenty lines and
     * closes it while the dock has no wifi, and every one of those queued
     * commands names this id. If the server minted its own, the whole batch
     * behind `receipt.open` would fail with `E_NOT_FOUND` the moment it drained.
     * Omit from the web, which is always online and reads the id back.
     */
    receiptId: uuidSchema.nullish(),
    poId: uuidSchema.nullish(),
    supplierId: uuidSchema.nullish(),
    /**
     * Accepted and ignored: a receipt with no `poId` IS a blind receipt, so the
     * flag carries no information the payload does not already have.
     */
    blind: z.boolean().nullish(),
    note: noteSchema.nullish(),
  })
  .refine((v) => Boolean(v.poId ?? v.supplierId), {
    message: 'poId or supplierId is required',
    path: ['poId'],
  });
export type ReceiptOpenInput = z.infer<typeof receiptOpenSchema>;

/**
 * One scanned/keyed receipt line.
 *
 * Pallet handling (ARCHITECTURE §8.1), in precedence order:
 *  1. `palletId` — an existing pallet resolved by a previous scan;
 *  2. `ssccAlias` — the SUPPLIER's SSCC. A pallet carrying that alias is
 *     reused, otherwise a new LPN pallet is created with it. No relabelling;
 *  3. `lpn` — a device-leased `LP1…` printed offline;
 *  4. `newPallet: true` — mint a server LPN;
 *  5. none of the above — loose goods on the receiving floor (no pallet).
 */
export const receiptLineAddSchema = z
  .object({
    receiptId: uuidSchema,
    /**
     * A line id the CLIENT minted, adopted verbatim. The scanner shows the line
     * in its local summary the instant it is scanned and must be able to name
     * it again (`receipt.lineVoid`) while still offline.
     */
    receiptLineId: uuidSchema.nullish(),
    poLineId: uuidSchema.nullish(),
    articleId: uuidSchema,
    qty: qtySchema.optional(),
    /**
     * Wire alias for `qty`. The scanner has already converted through
     * `qtyPerScan` and states base units explicitly, so it never sends a
     * `unitId`; the name says which of the two it means.
     */
    qtyBase: qtySchema.optional(),
    /** Omit for base units. Converted server-side via `article_units`. */
    unitId: uuidSchema.nullish(),
    lotNo: lotNoSchema.nullish(),
    bestBefore: isoDateSchema.nullish(),
    palletId: uuidSchema.nullish(),
    ssccAlias: z.string().trim().min(8).max(30).nullish(),
    lpn: z.string().trim().max(20).nullish(),
    newPallet: z.boolean().default(false),
    /** Defaults to the warehouse's receiving floor bin. */
    toBinId: uuidSchema.nullish(),
    /** Wire alias for `toBinId` — the bin the scanner is receiving into. */
    binId: uuidSchema.nullish(),
    /** Provenance: what was physically scanned to produce this line. */
    scannedBarcode: z.string().max(500).nullish(),
  })
  .transform((v) => ({ ...v, qty: v.qty ?? v.qtyBase, toBinId: v.toBinId ?? v.binId }))
  .refine((v) => v.qty !== undefined, { message: 'qty (or qtyBase) is required', path: ['qty'] });
/** The WIRE shape (`z.input`): `qtyBase`/`binId` are aliases folded by `.parse()`. */
export type ReceiptLineAddInput = z.input<typeof receiptLineAddSchema>;

export interface ReceiptLineAddResult {
  receiptLineId: string;
  articleId: string;
  lotId: string;
  lotNo: string;
  bestBefore: string | null;
  palletId: string | null;
  lpn: string | null;
  binId: string;
  binCode: string;
  qtyBase: string;
  movementId: string;
  /** True when the lot already existed with a DIFFERENT best-before (§9 of the stock README). */
  bestBeforeMismatch: boolean;
}

/** Undo a mis-scan: reverses the receive with an `adjust` at the same bin. */
export const receiptLineVoidSchema = z.object({
  receiptId: uuidSchema,
  receiptLineId: uuidSchema,
  reason: noteSchema.nullish(),
});
export type ReceiptLineVoidInput = z.infer<typeof receiptLineVoidSchema>;

export const receiptCloseSchema = z.object({
  receiptId: uuidSchema,
  /** Close the PO even if lines are under-received (the goods are not coming). */
  closePurchaseOrder: z.boolean().default(false),
  note: noteSchema.nullish(),
});
export type ReceiptCloseInput = z.infer<typeof receiptCloseSchema>;

export interface ReceiptCloseResult {
  receiptId: string;
  poId: string | null;
  poStatus: PurchaseOrderStatus | null;
  lines: {
    poLineId: string;
    /** What THIS receipt added — the quantity posted to BC. */
    receivedQtyBase: string;
    /** The PO line's running total after this receipt, for reconciliation. */
    totalReceivedQtyBase: string;
  }[];
  /** True when a BC `po.receipt` message was queued. */
  erpQueued: boolean;
}

/**
 * FCL palletize: container goods arrive loose, we build N identical pallets and
 * print one label per pallet in a single batch job (spec §5.7).
 */
export const receiptPalletizeSchema = z
  .object({
    receiptId: uuidSchema,
    poLineId: uuidSchema.nullish(),
    articleId: uuidSchema,
    lotNo: lotNoSchema.nullish(),
    bestBefore: isoDateSchema.nullish(),
    palletCount: z.number().int().min(1).max(200).optional(),
    /** Base units on EACH pallet. */
    qtyPerPallet: qtySchema.optional(),
    /**
     * Device-leased pallets: the scanner minted the ids AND the `LP1…` numbers
     * from its lease, printed the labels offline and stuck them on the pallets
     * before this command was ever sent. When present, the server adopts them
     * instead of allocating `LP0…` numbers of its own — anything else makes the
     * physical label a lie. `palletCount`/`qtyPerPallet` are then derived from
     * the array and need not be sent.
     */
    pallets: z
      .array(
        z.object({
          palletId: uuidSchema,
          lpn: z.string().trim().min(3).max(20),
          qtyBase: qtySchema,
        }),
      )
      .min(1)
      .max(200)
      .optional(),
    /** Cross-check only: the scanner's own sum over `pallets`. */
    totalQtyBase: qtySchema.optional(),
    toBinId: uuidSchema.nullish(),
    /** Wire alias for `toBinId`. */
    binId: uuidSchema.nullish(),
    printLabels: z.boolean().default(true),
    printerId: uuidSchema.nullish(),
  })
  .transform((v) => ({
    ...v,
    toBinId: v.toBinId ?? v.binId,
    palletCount: v.palletCount ?? v.pallets?.length,
    qtyPerPallet: v.qtyPerPallet ?? v.pallets?.[0]?.qtyBase,
  }))
  .refine((v) => v.palletCount !== undefined && v.palletCount >= 1, {
    message: 'palletCount (or a non-empty pallets array) is required',
    path: ['palletCount'],
  })
  .refine((v) => v.qtyPerPallet !== undefined, {
    message: 'qtyPerPallet (or pallets[].qtyBase) is required',
    path: ['qtyPerPallet'],
  })
  .refine((v) => !v.pallets || v.pallets.every((p) => p.qtyBase === v.pallets![0]!.qtyBase), {
    message: 'palletize builds N IDENTICAL pallets — every pallets[].qtyBase must match',
    path: ['pallets'],
  });
/** The WIRE shape (`z.input`): `pallets`/`binId` are aliases folded by `.parse()`. */
export type ReceiptPalletizeInput = z.input<typeof receiptPalletizeSchema>;

export interface ReceiptPalletizeResult {
  receiptId: string;
  lotId: string;
  pallets: { palletId: string; lpn: string; receiptLineId: string }[];
  binId: string;
  totalQtyBase: string;
  /** Print job id from the print port, or null when printing was skipped/unavailable. */
  printJobId: string | null;
}

/**
 * Produce fast path (spec §5.17): vegetables/milk are received and moved
 * straight to the packing floor. The lot is derived — `<supplier-code>-<date>` —
 * because the boxes carry no batch label at all.
 */
export const receiptProduceReceiveSchema = z.object({
  receiptId: uuidSchema,
  poLineId: uuidSchema.nullish(),
  articleId: uuidSchema,
  qty: qtySchema,
  unitId: uuidSchema.nullish(),
  /** Delivery date that identifies the batch. Defaults to today (Europe/Stockholm). */
  deliveryDate: isoDateSchema.nullish(),
  /** Target packing-floor bin. Defaults to the area flagged `floor_role = 'packing'`. */
  toBinId: uuidSchema.nullish(),
  scannedBarcode: z.string().max(500).nullish(),
});
export type ReceiptProduceReceiveInput = z.infer<typeof receiptProduceReceiveSchema>;

export interface ReceiptProduceReceiveResult {
  receiptLineId: string;
  lotId: string;
  lotNo: string;
  qtyBase: string;
  binId: string;
  binCode: string;
  movementIds: string[];
}

export interface ReceiptLineView {
  id: string;
  receiptId: string;
  poLineId: string | null;
  articleId: string;
  articleNo: string;
  articleName: string;
  lotId: string;
  lotNo: string;
  bestBefore: string | null;
  palletId: string | null;
  lpn: string | null;
  ssccAlias: string | null;
  qtyBase: string;
  createdAt: string;
}

export interface ReceiptListItem {
  id: string;
  poId: string | null;
  poNo: string | null;
  supplierId: string;
  supplierName: string;
  status: ReceiptStatus;
  startedBy: string | null;
  startedByName: string | null;
  lineCount: number;
  note: string | null;
  createdAt: string;
  closedAt: string | null;
}

export interface ReceiptDetail extends ReceiptListItem {
  lines: ReceiptLineView[];
  /** PO lines with their remaining open quantity — the auto-filtered pick list. */
  poLines: PurchaseOrderLineView[];
}

// ---------------------------------------------------------------------------
// scan resolution (§5.7 "scan anything")
// ---------------------------------------------------------------------------

export const receivingScanSchema = z.object({
  code: scanCodeSchema,
  /** Narrows raw (supplier-scoped) barcode lookups and the PO-line filter. */
  receiptId: uuidSchema.nullish(),
  poId: uuidSchema.nullish(),
  supplierId: uuidSchema.nullish(),
});
export type ReceivingScanInput = z.infer<typeof receivingScanSchema>;

export const receivingScanKindSchema = z.enum(['gs1', 'ean', 'raw']);
export type ReceivingScanKind = z.infer<typeof receivingScanKindSchema>;

/**
 * What the scan turned out to be. Every branch the receiving UI has to handle
 * is a field here — the client never re-parses the barcode itself.
 */
export interface ReceivingScanResult {
  kind: ReceivingScanKind;
  /** The payload after symbology-identifier/transport-noise removal. */
  data: string;
  gtin14: string | null;
  lotNo: string | null;
  bestBefore: string | null;
  sscc: string | null;
  /** AI 37/30 count, or AI 310n net weight in kg, when the label carried one. */
  quantity: string | null;
  quantityUnit: 'count' | 'kg' | null;
  /** Structured GS1 warnings (never thrown, never silent). */
  warnings: { code: string; message: string }[];

  /** Article the barcode maps to, when exactly one matched. */
  article: {
    articleId: string;
    articleNo: string;
    name: string;
    baseUnitId: string;
    baseUnitCode: string;
    lotTracked: boolean;
    expiryRequired: boolean;
    catchWeight: boolean;
    /** From `article_barcodes.qty_per_scan` — one scan means this many units. */
    qtyPerScan: string;
    unitId: string | null;
    unitCode: string | null;
    /** True when the code resolved through a supplier-scoped raw barcode. */
    supplierScoped: boolean;
  } | null;
  /** Set when the barcode maps to more than one article (`E_BARCODE_AMBIGUOUS` territory). */
  ambiguousArticleIds: string[];

  /** PO lines that match the resolved article — the "right order appears" step. */
  poLines: PurchaseOrderLineView[];

  /** Scanned an SSCC or one of our LPNs: the pallet it resolves to. */
  pallet: {
    palletId: string;
    lpn: string;
    ssccAlias: string | null;
    status: string;
    currentBinId: string | null;
    currentBinCode: string | null;
  } | null;
  /** True when the SSCC is unknown — receiving may adopt it as an alias. */
  ssccUnknown: boolean;

  /** Scanned a bin code (put-away target). */
  bin: { binId: string; binCode: string; isBlocked: boolean } | null;

  /**
   * Nothing matched. The UI offers the teach flow when the operator holds
   * `warehouse.barcodes.teach`.
   */
  needsTeach: boolean;
}

export const receivingBarcodeTeachSchema = z.object({
  barcode: z.string().trim().min(3).max(200),
  articleId: uuidSchema,
  /** Raw (non-GS1) codes are only unique within a supplier — pass it. */
  supplierId: uuidSchema.nullish(),
  unitId: uuidSchema.nullish(),
  qtyPerScan: qtySchema.default('1'),
  /** Omitted ⇒ inferred: a valid GTIN becomes `gtin`, anything else `raw`. */
  kind: z.enum(['gtin', 'raw']).nullish(),
  note: noteSchema.nullish(),
});
export type ReceivingBarcodeTeachInput = z.infer<typeof receivingBarcodeTeachSchema>;

export interface ReceivingBarcodeTeachResult {
  barcodeId: string;
  barcode: string;
  kind: 'gtin' | 'raw';
  articleId: string;
  supplierId: string | null;
  qtyPerScan: string;
  created: boolean;
}

// ---------------------------------------------------------------------------
// put-away
// ---------------------------------------------------------------------------

/**
 * Scan pallet → scan bin. Identify either side by id or by the code that was
 * physically scanned; `observedFromBinId` is a scanner OBSERVATION, verified by
 * the ledger rather than trusted (ARCHITECTURE §6).
 */
export const putawaySchema = z
  .object({
    palletId: uuidSchema.nullish(),
    /** LPN **or** supplier SSCC — both resolve to the same pallet. */
    palletCode: z.string().trim().min(3).max(40).nullish(),
    toBinId: uuidSchema.nullish(),
    toBinCode: z.string().trim().min(1).max(40).nullish(),
    observedFromBinId: uuidSchema.nullish(),
    note: noteSchema.nullish(),
  })
  .refine((v) => Boolean(v.palletId ?? v.palletCode), {
    message: 'palletId or palletCode is required',
    path: ['palletId'],
  })
  .refine((v) => Boolean(v.toBinId ?? v.toBinCode), {
    message: 'toBinId or toBinCode is required',
    path: ['toBinId'],
  });
export type PutawayInput = z.infer<typeof putawaySchema>;

export interface PutawayResult {
  palletId: string;
  lpn: string;
  fromBinId: string | null;
  toBinId: string;
  toBinCode: string;
  movementIds: string[];
  /** Zero movements = an empty pallet was relocated; that is legal, not an error. */
  movedLines: number;
}

export interface PutawayQueueItem {
  palletId: string;
  lpn: string;
  ssccAlias: string | null;
  binId: string;
  binCode: string;
  articleCount: number;
  totalQtyBase: string;
  /** Newest receipt line that put stock on this pallet. */
  receivedAt: string | null;
  articles: { articleId: string; articleNo: string; name: string; qtyBase: string }[];
}

// ---------------------------------------------------------------------------
// CO2 cage sessions (spec §5.17)
// ---------------------------------------------------------------------------

export const cageCodeSchema = z.string().trim().min(2).max(64);
export const assetSerialSchema = z.string().trim().min(2).max(64);

export const cageScanSchema = z.object({
  cageCode: cageCodeSchema,
  serial: assetSerialSchema,
  receiptId: uuidSchema.nullish(),
});
export type CageScanInput = z.infer<typeof cageScanSchema>;

export interface CageScanResult {
  cageCode: string;
  serial: string;
  assetId: string;
  /** `added` = new tube, `moved` = tube re-assigned from another cage. */
  outcome: 'added' | 'moved';
  /** Live count of tubes in this cage after the scan. */
  count: number;
}

/**
 * `receipt.cageSession` — the whole cage in ONE command.
 *
 * The scanner counts a cage of CO2 tubes offline (`ReceiveFlow.cageCommand`):
 * it holds the serials in a local session and emits them as one payload when
 * the operator closes the cage. Draining that as N `receipt.cageScan` commands
 * would be N transactions, N dedupe rows and N chances for the batch to stop
 * half-counted; as one command the cage is either counted or it is not.
 *
 * Duplicate serials inside one session are the device's own double-trigger, not
 * an operator mistake worth rejecting a 60-tube cage over: they are counted
 * once and reported in the heal note. A serial that belongs to ANOTHER cage
 * moves, exactly as `receipt.cageScan` does.
 */
export const cageSessionSchema = z.object({
  /** Device-minted session id, for idempotency across a re-send. */
  sessionId: uuidSchema.nullish(),
  cageCode: cageCodeSchema,
  serials: z.array(assetSerialSchema).min(1).max(2000),
  receiptId: uuidSchema.nullish(),
  supplierId: uuidSchema.nullish(),
  binId: uuidSchema.nullish(),
  /** Only CO2 tubes exist today; the field keeps the payload self-describing. */
  kind: z.literal('co2_tube').default('co2_tube'),
  /** When given, a mismatch is reported back as a healing note, never silently. */
  expectedCount: z.number().int().min(0).max(10000).nullish(),
});
export type CageSessionInput = z.infer<typeof cageSessionSchema>;

export interface CageSessionResult {
  cageCode: string;
  /** Tubes in the cage after the session. */
  count: number;
  added: number;
  moved: number;
  /** Serials sent more than once in the same payload. */
  duplicates: string[];
  expectedCount: number | null;
  countMatches: boolean;
}

export const cageCloseSchema = z.object({
  cageCode: cageCodeSchema,
  receiptId: uuidSchema.nullish(),
  /** When given, a mismatch is reported back as a healing note, never silently. */
  expectedCount: z.number().int().min(0).max(10000).nullish(),
});
export type CageCloseInput = z.infer<typeof cageCloseSchema>;

export interface CageCloseResult {
  cageCode: string;
  count: number;
  expectedCount: number | null;
  countMatches: boolean;
}

export interface CageSummary {
  cageCode: string;
  count: number;
  serials: { assetId: string; serial: string; status: string; lastMovementAt: string | null }[];
}
